import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
    catchError,
    EMPTY,
    exhaustMap,
    map,
    of,
    switchMap,
    withLatestFrom,
} from "rxjs";
import { ProfileApiService } from "../data-access/profile-api.service";
import { ProfileActions } from "./profile.actions";
import { profileFeature } from "./profile.reducer";

@Injectable()
export class ProfileEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly api = inject(ProfileApiService);
    readonly user$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileActions.opened),
            withLatestFrom(
                this.store.select(profileFeature.selectProfileState)
            ),
            switchMap(([{ username }, state]) =>
                state.user
                    ? EMPTY
                    : this.api.loadUser(username).pipe(
                          map((user) => ProfileActions.userLoaded({ user })),
                          catchError(() => of(ProfileActions.userLoadFailed()))
                      )
            )
        )
    );
    readonly initialGallery$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileActions.opened),
            withLatestFrom(
                this.store.select(profileFeature.selectProfileState)
            ),
            switchMap(([{ username, refreshGallery }, state]) =>
                !refreshGallery && state.latestId !== undefined && !state.galleryError
                    ? EMPTY
                    : this.api.loadOwnedGallery(username).pipe(
                          map(({ files, latestId }) =>
                              ProfileActions.galleryPageLoaded({
                                  username,
                                  files,
                                  latestId,
                                  initial: true,
                              })
                          ),
                          catchError(() =>
                              of(ProfileActions.galleryLoadFailed())
                          )
                      )
            )
        )
    );
    readonly moreGallery$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileActions.galleryLoadMoreRequested),
            withLatestFrom(
                this.store.select(profileFeature.selectProfileState)
            ),
            exhaustMap(([, state]) =>
                !state.username || !state.latestId
                    ? of(ProfileActions.galleryLoadFailed())
                    : this.api
                          .loadOwnedGallery(state.username, state.latestId)
                          .pipe(
                              map(({ files, latestId }) =>
                                  ProfileActions.galleryPageLoaded({
                                      username: state.username!,
                                      files,
                                      latestId,
                                      initial: false,
                                  })
                              ),
                              catchError(() =>
                                  of(ProfileActions.galleryLoadFailed())
                              )
                          )
            )
        )
    );
    readonly bundles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileActions.bundlesRequested),
            withLatestFrom(
                this.store.select(profileFeature.selectProfileState)
            ),
            exhaustMap(([{ username, filterUsername, initial }, state]) =>
                this.api
                    .loadBundles(
                        filterUsername,
                        initial ? null : state.bundlesLatestId
                    )
                    .pipe(
                        map(({ bundles, latestId }) =>
                            ProfileActions.bundlesPageLoaded({
                                username,
                                bundles,
                                latestId,
                                initial,
                            })
                        ),
                        catchError(() => of(ProfileActions.bundlesLoadFailed()))
                    )
            )
        )
    );
    readonly activities$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileActions.activitiesRequested),
            withLatestFrom(
                this.store.select(profileFeature.selectProfileState)
            ),
            exhaustMap(([{ username, initial }, state]) =>
                this.api
                    .loadActivities(
                        username,
                        initial ? null : state.activitiesLatestId
                    )
                    .pipe(
                        map(({ activities, latestId }) =>
                            ProfileActions.activitiesPageLoaded({
                                username,
                                activities,
                                latestId,
                                initial,
                            })
                        ),
                        catchError(() =>
                            of(ProfileActions.activitiesLoadFailed())
                        )
                    )
            )
        )
    );
}
